import React from 'react';
import Spinner from '@atlaskit/spinner';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { ButtonProps, ButtonSize } from '../index';
import { buttonIconContainerStyles } from '../styles/sharedButtonStyles';
import { getBasicStyles, getIconSpacingStyles, getResponsiveStyles, getStateStyles } from '../utils/buttonStyleHelpers';

interface GenericButtonProps extends ButtonProps {
  clickHandler: (e: React.MouseEvent<HTMLElement>) => void;
  testId: string;
  showBeforeIcon: React.ReactElement | false;
  showAfterIcon: React.ReactElement | false;
  isButtonWithoutIcon: boolean;
  isResponsiveSpacing: boolean;
  href?: string;
  target?: string;
}

export const GenericButton: React.FC<GenericButtonProps> = ({
  label,
  type = 'button',
  appearance,
  disabled = false,
  isLoading = false,
  clickHandler,
  testId,
  fullWidth = false,
  selected = false,
  size = ButtonSize.MEDIUM,
  showBeforeIcon,
  showAfterIcon,
  isButtonWithoutIcon,
  isResponsiveSpacing,
  href,
  target,
}) => {
  const commonProps = {
    'aria-label': label.replaceAll('-', ' '),
    'data-testid': testId,
    css: [
      ...getBasicStyles(appearance!, size),
      ...getIconSpacingStyles(size, isButtonWithoutIcon, showBeforeIcon, showAfterIcon),
      ...getResponsiveStyles(isResponsiveSpacing, size, showBeforeIcon, showAfterIcon),
      ...getStateStyles(selected, isLoading, disabled, fullWidth),
    ],
    onClick: clickHandler,
  };

  const content = (
    <>
      {isLoading && <Spinner testId="spinner" size="small" />}
      {showBeforeIcon && (
        <span css={[buttonIconContainerStyles, css({ marginRight: token('space.050') })]}>{showBeforeIcon}</span>
      )}
      <span css={[isLoading && css({ marginLeft: token('space.100') })]}>{label}</span>
      {showAfterIcon && (
        <span css={[buttonIconContainerStyles, css({ marginLeft: token('space.050') })]}>{showAfterIcon}</span>
      )}
    </>
  );

  if (href && !disabled) {
    return (
      <a {...commonProps} href={href} target={target} rel={target === '_blank' ? 'noreferrer' : ''}>
        {content}
      </a>
    );
  }

  return (
    <button {...commonProps} type={type} disabled={disabled || isLoading}>
      {content}
    </button>
  );
};
